
import React, { useState, useEffect } from 'react';
import { UserProfile, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { MessageSquareHeart, Target, ArrowLeft, Sparkles, Award, Brain, Send, Loader2, Users, Baby, Briefcase, UserPlus, Heart, ShieldAlert, GraduationCap, ChevronRight, Info, Star } from 'lucide-react';
import { sendMessageToHawre } from '../services/geminiService';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

type SocialGroup = 'elders' | 'kids' | 'job' | 'strangers' | 'difficult' | 'intimate' | null;

const SocialHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeGroup, setActiveGroup] = useState<SocialGroup>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simMessage, setSimMessage] = useState('');
  const [simHistory, setSimHistory] = useState<{role: string, text: string}[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  const t = TRANSLATIONS[user.language] || TRANSLATIONS[Language.KURDISH];

  const socialGroups = [
    { id: 'elders', label: user.language === Language.KURDISH ? 'کەسانی بەتەمەن' : 'سالمندان', icon: Users, color: 'text-amber-600', bg: 'bg-amber-50', desc: user.language === Language.KURDISH ? 'هونەری قسەکردن لەگەڵ گەورەکان' : 'هنر گفتگو با بزرگترها', principles: user.language === Language.KURDISH ? ['ڕێز و سەبر', 'بەکارنەهێنانی وشەی زۆر نوێ', 'گوێگرتنی چالاک'] : ['احترام و صبر', 'استفاده نکردن از کلمات خیلی جدید', 'گوش دادن فعال'] },
    { id: 'kids', label: user.language === Language.KURDISH ? 'منداڵان' : 'کودکان', icon: Baby, color: 'text-rose-500', bg: 'bg-rose-50', desc: user.language === Language.KURDISH ? 'زمان و جیهانی منداڵان' : 'زبان و دنیای کودکان', principles: user.language === Language.KURDISH ? ['سادەیی ڕستەکان', 'بەشداری لە خەیاڵ', 'بەکارهێنانی ئاماژەی جەستە'] : ['سادگی جملات', 'همراهی در تخیل', 'استفاده از حرکات بدن'] },
    { id: 'job', label: user.language === Language.KURDISH ? 'کار و چاوپێکەوتن' : 'کار و مصاحبه', icon: Briefcase, color: 'text-blue-600', bg: 'bg-blue-50', desc: user.language === Language.KURDISH ? 'بەرەو سەرکەوتنی پیشەیی' : 'به سوی موفقیت شغلی', principles: user.language === Language.KURDISH ? ['کورتی و پوختی', 'نیشاندانی تواناکان', 'زمانی جەستەی جێگیر'] : ['خلاصه و مفید بودن', 'نشان دادن توانمندی‌ها', 'زبان بدن استوار'] },
    { id: 'strangers', label: user.language === Language.KURDISH ? 'کەسانی نامۆ' : 'غریبه‌ها', icon: UserPlus, color: 'text-emerald-600', bg: 'bg-emerald-50', desc: user.language === Language.KURDISH ? 'شکاندنی سەهۆڵی گفتوگۆ' : 'شکستن یخ گفتگو', principles: user.language === Language.KURDISH ? ['بزەخەندە', 'پرسیاری کراوە', 'ڕێزگرتن لە مەودای کەسی'] : ['لبخند زدن', 'سوالات باز', 'احترام به حریم شخصی'] },
    { id: 'difficult', label: user.language === Language.KURDISH ? 'کەسانی توڕە' : 'افراد دشوار', icon: ShieldAlert, color: 'text-orange-600', bg: 'bg-orange-50', desc: user.language === Language.KURDISH ? 'بەڕێوەبردنی ململانێکان' : 'مدیریت تعارضات', principles: user.language === Language.KURDISH ? ['هێمن مانەوە', 'دانبەخۆداگرتن', 'گەیشتن بە چارەسەر'] : ['حفظ آرامش', 'کنترل احساسات', 'رسیدن به راه حل'] },
    { id: 'intimate', label: user.language === Language.KURDISH ? 'هاوبەشی ژیان' : 'شریک زندگی', icon: Heart, color: 'text-indigo-600', bg: 'bg-indigo-50', desc: user.language === Language.KURDISH ? 'قسەی دڵ و سۆز' : 'گفتگوی صمیمانه', principles: user.language === Language.KURDISH ? ['ڕاشکاوی', 'پشتگیری سۆزداری', 'هاوبەشی یادەوەری'] : ['صداقت کامل', 'حمایت عاطفی', 'اشتراک‌گذاری خاطرات'] },
  ];

  const startSimulation = () => {
    setIsSimulating(true);
    setSimHistory([]);
    setFeedback(null);
    const initialPrompts: Record<string, string> = {
      elders: user.language === Language.KURDISH ? "سڵاو ڕۆڵە گیان، چۆنیت؟ وەزعی ئیش و کارت چۆنە؟" : "سلام فرزندم، چطوری؟ اوضاع کار و بارت چطوره؟",
      kids: user.language === Language.KURDISH ? "سڵااااو! سەیری ئەم یارییە بکە، دێیت لەگەڵم یاری بکەیت؟" : "سلاااام! این بازی رو ببین، میای با هم بازی کنیم؟",
      job: user.language === Language.KURDISH ? "سڵاو، بەخێربێیت بۆ چاوپێکەوتنەکە. تکایە کەمێک باسی خۆتم بۆ بکە." : "سلام، به مصاحبه خوش آمدید. لطفا کمی از خودتان برایم بگویید.",
      strangers: user.language === Language.KURDISH ? "ببورە، کاتت هەیە؟ دەزانیت نزیکترین کتێبخانە لەکوێیە؟" : "ببخشید، وقت دارید؟ می‌دونید نزدیک‌ترین کتابخانه کجاست؟",
      difficult: user.language === Language.KURDISH ? "من زۆر لەم خزمەتگوزارییە ناڕازیم! بۆچی تا ئێستا ئیشەکەم تەواو نەبووە؟" : "من واقعاً از این سرویس ناڕازیم! چرا تا الان کار من تموم نشده؟",
      intimate: user.language === Language.KURDISH ? "هەست دەکەم کەمێک دوورکەوتووینەتەوە لە یەک، حەز دەکەم کەمێک قسە بکەین." : "احساس می‌کنم کمی از هم دور شدیم، دوست دارم کمی با هم حرف بزنیم.",
    };
    setSimHistory([{ role: 'model', text: initialPrompts[activeGroup!] }]);
  };

  const handleSimulate = async () => {
    if (!simMessage.trim() || !activeGroup) return;
    const userMsg = simMessage;
    setSimMessage('');
    const newHistory = [...simHistory, { role: 'user', text: userMsg }];
    setSimHistory(newHistory);

    try {
      const response = await sendMessageToHawre(user, [], 
        `[SOCIAL ACADEMY ROLEPLAY] You are a person from the group: ${activeGroup}. 
        Stay in character strictly. User says: ${userMsg}. 
        Keep it brief and conversational. Use language: ${user.language === 'fa' ? 'Persian' : 'Kurdish Sorani'}.`
      );
      setSimHistory([...newHistory, { role: 'model', text: response }]);
    } catch (e) {
      console.error(e);
    }
  };

  const getFinalFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const transcript = simHistory.map(m => `${m.role}: ${m.text}`).join('\n');
      const response = await sendMessageToHawre(user, [], 
        `[SOCIAL ANALYSIS] Analyze this roleplay session as a social coach. 
        Segment: ${activeGroup}. 
        Transcript: ${transcript}. 
        Give 2 strengths and 1 improvement tip. Language: ${user.language === 'fa' ? 'Persian' : 'Kurdish Sorani'}.`
      );
      setFeedback(response);
    } catch (e) {
      setFeedback("Error generating feedback.");
    } finally {
      setLoadingFeedback(false);
    }
  };

  if (isSimulating) {
    const groupData = socialGroups.find(g => g.id === activeGroup);
    return (
      <div className="h-full flex flex-col bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl overflow-hidden animate-fade-in relative">
        <div className={`p-6 border-b flex items-center justify-between ${groupData?.bg}`}>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSimulating(false)} 
              className="p-2 hover:bg-white/50 rounded-full transition-colors flex items-center gap-2 text-slate-600 font-bold"
            >
              <ArrowLeft className="w-5 h-5 rtl:rotate-180" />
              <span className="hidden sm:inline">{t.back}</span>
            </button>
            <div className={`p-3 rounded-2xl bg-white shadow-sm ${groupData?.color}`}><groupData.icon className="w-6 h-6" /></div>
            <div>
              <h3 className="font-black text-slate-800">{groupData?.label}</h3>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{t.social_start_sim}</span>
            </div>
          </div>
          <button onClick={getFinalFeedback} className="bg-slate-900 text-white px-5 py-2 rounded-xl text-xs font-black shadow-lg hover:bg-slate-800 transition-all flex items-center gap-2">
            <Target className="w-4 h-4" /> {user.language === Language.KURDISH ? 'تەواوکردن و شیکاری' : 'پایان و تحلیل'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/30 no-scrollbar">
          {simHistory.map((msg, i) => {
            const isModel = msg.role === 'model';
            return (
              <div key={i} className={`flex ${isModel ? 'justify-start' : 'justify-end'} animate-slide-up`}>
                <div className={`max-w-[85%] p-4 rounded-[2rem] shadow-sm text-sm leading-relaxed ${isModel ? 'bg-white border border-slate-100 text-slate-800 rounded-tl-none' : 'bg-indigo-600 text-white rounded-tr-none shadow-indigo-200'}`}>
                  {msg.text}
                </div>
              </div>
            );
          })}
          {simHistory.length === 0 && <div className="h-full flex items-center justify-center text-slate-300 italic">{user.language === Language.KURDISH ? 'چاوەڕێی دەستپێکردنم...' : 'در انتظار شروع...'}</div>}
        </div>

        {feedback && (
          <div className="absolute inset-x-6 bottom-24 z-20 bg-white/95 backdrop-blur-md p-6 rounded-[2rem] border-2 border-indigo-100 shadow-2xl animate-slide-up">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-black text-indigo-600 flex items-center gap-2"><Sparkles className="w-5 h-5" /> {t.social_feedback_title}</h4>
              <button 
                onClick={() => { setFeedback(null); setIsSimulating(false); setActiveGroup(null); }} 
                className="p-2 hover:bg-slate-100 rounded-full flex items-center gap-2 text-slate-500 font-bold"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs">{t.back}</span>
              </button>
            </div>
            <p className="text-sm text-slate-700 leading-loose whitespace-pre-wrap">{feedback}</p>
          </div>
        )}

        <div className="p-6 bg-white border-t border-slate-100">
          <div className="flex gap-2">
            <input 
              value={simMessage}
              onChange={(e) => setSimMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSimulate()}
              placeholder={user.language === Language.KURDISH ? 'وەڵامەکەت بنووسە...' : 'پاسخ خود را بنویسید...'}
              className="flex-1 bg-slate-50 rounded-2xl px-6 py-4 outline-none border border-slate-100 focus:ring-4 focus:ring-indigo-50 transition-all"
            />
            <button 
              onClick={handleSimulate} 
              disabled={!simMessage.trim()} 
              aria-label={t.send}
              className="bg-indigo-600 text-white p-4 rounded-2xl shadow-xl shadow-indigo-200 hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
            >
              <Send className="w-6 h-6 rtl:-rotate-90" />
              <span className="hidden sm:inline font-bold">{t.send}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (activeGroup) {
    const groupData = socialGroups.find(g => g.id === activeGroup);
    return (
      <div className="animate-fade-in space-y-8 pb-20">
        <button onClick={() => setActiveGroup(null)} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-black transition-colors bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm">
          <ArrowLeft className="w-5 h-5 rtl:rotate-180" />
          {t.social_back_to_groups}
        </button>

        <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full blur-3xl opacity-50 -mr-32 -mt-32"></div>
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row items-center gap-8 mb-10 text-center md:text-right">
              <div className={`w-24 h-24 rounded-[2rem] ${groupData?.bg} ${groupData?.color} flex items-center justify-center shadow-lg transform rotate-3`}>
                {groupData && <groupData.icon className="w-12 h-12" />}
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-black text-slate-800 mb-2">{groupData?.label}</h2>
                <p className="text-slate-500 text-lg leading-relaxed">{groupData?.desc}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
               <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
                  <h4 className="font-black text-slate-800 mb-6 flex items-center gap-2"><GraduationCap className="w-5 h-5 text-indigo-500" /> {t.social_principles}</h4>
                  <ul className="space-y-4">
                    {groupData?.principles.map((p, i) => (
                      <li key={i} className="flex items-center gap-4 bg-white p-3 rounded-xl border border-slate-100 shadow-sm group">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-black group-hover:scale-110 transition-transform">{i+1}</div>
                        <span className="text-sm font-bold text-slate-700">{p}</span>
                      </li>
                    ))}
                  </ul>
               </div>
               <div className="bg-gradient-to-br from-indigo-900 to-slate-900 p-8 rounded-[2rem] text-white flex flex-col justify-center text-center">
                  <div className="mb-6 opacity-30"><Brain className="w-16 h-16 mx-auto" /></div>
                  <h4 className="text-xl font-bold mb-4">{user.language === Language.KURDISH ? 'ئامادەیت بۆ تاقیکردنەوە؟' : 'برای چالش آماده‌ای؟'}</h4>
                  <p className="text-indigo-200 text-sm mb-8">{user.language === Language.KURDISH ? 'هاوڕێ دەبێت بە کەسایەتی ئەو گرووپە و گفتوگۆت لەگەڵ دەکات.' : 'هاورێ در نقش آن فرد ظاهر می‌شود و با شما گفتگو می‌کند.'}</p>
                  <button onClick={startSimulation} className="w-full bg-white text-indigo-900 py-4 rounded-2xl font-black text-lg shadow-xl shadow-black/20 hover:scale-[1.02] transition-all">{t.social_start_sim}</button>
               </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="mb-10 text-right">
        <div className="flex items-center justify-end gap-3 mb-4">
          <h2 className="text-3xl font-black text-slate-800">{t.social_academy_title}</h2>
          <div className="p-4 bg-orange-100 text-orange-600 rounded-[1.5rem] shadow-sm"><GraduationCap className="w-8 h-8" /></div>
        </div>
        <p className="text-slate-500 text-base font-medium leading-relaxed max-w-2xl mr-auto">
          {t.social_academy_desc}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {socialGroups.map((group) => (
          <button 
            key={group.id} 
            onClick={() => setActiveGroup(group.id as SocialGroup)} 
            className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-2xl hover:scale-[1.02] transition-all text-right group flex flex-col items-start h-full"
          >
            <div className={`w-16 h-16 rounded-2xl ${group.bg} ${group.color} flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform shadow-sm`}>
              <group.icon className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-2">{group.label}</h3>
            <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6">
              {group.desc}
            </p>
            <div className="mt-auto flex items-center gap-2 text-indigo-600 text-xs font-black uppercase tracking-wider group-hover:translate-x-[-8px] transition-transform">
              <span>{t.social_start_sim}</span>
              <ChevronRight className="w-4 h-4 rtl:rotate-180" />
            </div>
          </button>
        ))}
      </div>

      <div className="mt-12 bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm flex flex-col md:flex-row items-center gap-8">
        <div className="w-20 h-20 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
          <Star className="w-10 h-10 text-indigo-500 animate-pulse" />
        </div>
        <div className="flex-1 text-center md:text-right">
          <h4 className="font-black text-slate-800 text-lg mb-2">{user.language === Language.KURDISH ? 'خاڵەکانی پەیوەندی کۆمەڵایەتی' : 'امتیاز روابط اجتماعی'}</h4>
          <p className="text-slate-500 text-sm">{user.language === Language.KURDISH ? 'هەرچەندە زیاتر ڕاهێنان بکەیت، متمانەت بەخۆت زیاتر دەبێت.' : 'هرچه بیشتر تمرین کنید، اعتماد به نفس کلامی شما بالاتر می‌رود.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-4xl font-black text-indigo-600">{user.socialTrainingProfile?.confidenceScore || 0}</span>
          <span className="text-slate-400 font-bold uppercase text-xs">/ 100</span>
        </div>
      </div>
    </div>
  );
};

export default SocialHub;
